'use client';
import type { PluginProjectRowMetric, ResourceMetersProps } from 'elowen-plugin-ui-kit';
import { useId, type ReactNode } from 'react';
import { Button } from './Button';
import { MetricDonut } from './MetricDonut';
import { usageProgressColour } from '../../lib/usageMeter';
import { useTranslation } from '../../lib/i18n';
import { formatUsageCount } from '../../lib/format';
import { horizontalEdgeFadeStyle, revealHorizontalItem, useHorizontalOverflow } from './horizontalScroll';

/** What sits in a ring with nothing measured behind it. An honest mark, never a zero: an environment
 *  whose sample has not arrived is not an environment using none of its share. */
const UNKNOWN_VALUE = '—';

/** The figure in the hole. CPU keeps one decimal below one per cent because that precision is useful at
 *  idle; capacity readings keep the compact `<1%` mark. The ring lifts either tiny fraction to a visible
 *  floor, while an exact zero keeps `0%` and no arc. */
export function centreValue(percent: number | null, locale: string, preciseFraction = false): string {
  if (percent === null) return UNKNOWN_VALUE;
  if (preciseFraction && percent > 0 && percent < 1) {
    return `${formatUsageCount(Math.max(0.1, Math.round(percent * 10) / 10), locale, 1)}%`;
  }
  const rounded = Math.round(percent);
  return rounded === 0 && percent > 0 ? '<1%' : `${rounded}%`;
}

/** The percentage an arc may claim: only a `ready` reading against a ceiling that exists. */
function readingOf(item: PluginProjectRowMetric): number | null {
  return item.state === 'ready' && typeof item.percent === 'number' && Number.isFinite(item.percent)
    ? Math.max(0, Math.min(100, item.percent))
    : null;
}

/** The sentence a reading is named by, in the tooltip and to a screen reader. */
function titleOf(item: PluginProjectRowMetric, percent: number | null, centre: string, locale: string): string {
  if (item.id === 'cpu' && percent !== null) return `${item.label}: ${centre}`;
  const value = item.valueText ?? `${item.label}: ${item.value}`;
  return percent === null ? value : value + ' · ' + formatUsageCount(percent, locale) + ' %';
}

/** ONE measured resource of the snapshot its owning plugin published, as a ring.
 *
 *  CPU, memory and disk are drawn with ONE grammar and one size. All three qualify for it because disk
 *  stopped being the odd reading out: it has its own ceiling now (`diskMb`, the fourth managed limit),
 *  so the plugin divides by that quota exactly as CPU and memory divide by theirs.
 *
 *  An ARC is drawn for `ready` and for nothing else, because `ready` is the only state carrying a
 *  percentage against a ceiling that exists. `absolute` is an equally real figure whose ceiling could not
 *  be read; `unknown`, `stopped` and `unavailable` carry no figure at all. All four keep the ring and
 *  lose the arc, so the strip holds its shape in every state and an arc never claims a proportion of
 *  something that was never measured.
 *
 *  The percentage lives in the hole, which is what lets three of these stand side by side in a card that
 *  is only ~270px wide on a phone: the value and the proportion occupy the same square instead of
 *  competing for one line. The exact pair — `640 MB / 1.0 GB` — is shown under the label when it says
 *  more than the percentage does, and is in the accessible text in every case. */
function ResourceAction({ item, description, compact = false, large = false, children }: {
  item: PluginProjectRowMetric; description: string; compact?: boolean; large?: boolean; children: ReactNode;
}) {
  const descriptionId = useId();
  if (!item.action) return children;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={item.action.label}
      aria-haspopup="dialog"
      aria-describedby={descriptionId}
      // The ghost hover wash is the foreground at 10 %, which in the light skin is the same grey as the ring's
      // track, so a hovered ring lost its remainder. The ring keeps its own surface on hover.
      className={compact
        ? 'h-auto w-auto gap-1 p-0 hover:bg-transparent'
        : `${large ? 'size-19' : 'size-13'} min-w-[var(--touch-target)] min-h-[var(--touch-target)] p-0 hover:bg-transparent`}
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onClick={(event) => { event.stopPropagation(); item.action!.onSelect(); }}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (!event.repeat) event.currentTarget.click();
        }
      }}
      onKeyUp={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
      }}
    >
      {children}
      <span id={descriptionId} className="sr-only">{description}</span>
    </Button>
  );
}

function ResourceMeter({ item, dense, large, locale }: { item: PluginProjectRowMetric; dense: boolean; large: boolean; locale: string }) {
  const percent = readingOf(item);
  // A producer whose reading is not a share (disk speed) puts its own figure in the hole; the arc still
  // shows the proportion. The figure belongs to a measured reading, never to a ring with nothing behind it.
  const centre = ['ready', 'absolute'].includes(item.state) && item.figure ? item.figure : centreValue(percent, locale, item.id === 'cpu');
  const title = titleOf(item, percent, centre, locale);
  // A plugin may add one compact identity beneath a metric, such as the host CPU model. Otherwise the line
  // says what the hole cannot; it disappears when it would only repeat the percentage or an unknown mark.
  const detail = item.description ?? (item.value === UNKNOWN_VALUE || (percent !== null && item.value === `${Math.round(percent)}%`)
    ? ''
    : item.value);
  // Dense strips keep the used capacity, with the full pair in the tooltip and accessible text.
  // A rate ring already shows its figure in the hole, so its caption is the unit alone; the share
  // stays in the tooltip and accessible text.
  const shown = item.figure ? detail
    : dense && !item.description ? detail.split(' / ')[0]! : detail;
  return (
    <div
      className={`flex flex-1 flex-col items-center gap-1 ${item.figure ? 'min-w-max' : large ? 'min-w-19' : 'min-w-13'}`}
      title={title}
      data-metric={item.id}
      data-metric-state={item.state}
    >
      <ResourceAction item={item} description={title} large={large}>
        <MetricDonut
          percent={percent}
          colour={usageProgressColour(percent ?? 0)}
          label={item.label}
          valueText={title}
          centre={centre}
          size={large ? 'large' : 'default'}
        />
      </ResourceAction>
      <span data-metric-label className="w-full truncate text-center text-eyebrow uppercase text-muted-foreground">
        {item.label}
      </span>
      {/* The slot is kept whether or not it is used, so the columns of the strip share a baseline
          however many of them have an exact pair to show. */}
      <span
        data-metric-caption
        className={`min-h-3 whitespace-nowrap ${item.figure ? 'w-max' : 'w-full truncate'} text-center text-small ${item.description ? 'text-muted-foreground' : 'tabular-nums text-foreground'}`}
        title={item.description}
      >
        {shown}
      </span>
    </div>
  );
}

/** A single caption line with a glyph-sized ring, label, measured value and share. CPU keeps its
 *  percentage. The full used/limit pair stays in the tooltip and accessible description. */
function CompactResourceMeter({ item, locale }: { item: PluginProjectRowMetric; locale: string }) {
  const percent = readingOf(item);
  const centre = centreValue(percent, locale, item.id === 'cpu');
  const title = titleOf(item, percent, centre, locale);
  const value = item.id === 'cpu' || !['ready', 'absolute'].includes(item.state) ? centre
    : item.figure ? `${item.figure} ${item.description ?? ''}` + (percent === null ? '' : ' · ' + formatUsageCount(percent, locale) + ' %')
    : item.value.split(' / ')[0] + (percent === null ? '' : ' · ' + centre);
  return (
    <span className="flex shrink-0 items-center gap-1 whitespace-nowrap" title={title} data-metric={item.id} data-metric-state={item.state}>
      <ResourceAction item={item} description={title} compact>
        <MetricDonut percent={percent} colour={usageProgressColour(percent ?? 0)} label={item.label} valueText={title} size="compact" />
        <span aria-hidden className="truncate text-small text-muted-foreground">
          <span data-metric-label className="text-eyebrow uppercase">{item.label}</span>
          {' '}
          <span className="tabular-nums text-foreground">{value}</span>
        </span>
      </ResourceAction>
    </span>
  );
}

/** Up to five rings of one register snapshot, on a native horizontally scrollable track.
 *  The figures are the ones the plugin last measured in every state: a refresh in flight only dims them,
 *  and a failed read only marks them, because replacing a measurement with a placeholder is how a
 *  populated environment reported nothing each time a poll missed. `compact` puts the same snapshot on one
 *  caption line, centred under what it sits under, for a surface where the strip is secondary to it.
 *  `large` is host-only, outside the plugin kit: bigger rings centred as the closing figure of a card that
 *  shows one computer's live load (the paired CLI device card). */
export function ResourceMeters({ metrics, size = 'default' }: Omit<ResourceMetersProps, 'size'> & { size?: ResourceMetersProps['size'] | 'large' }) {
  const { locale } = useTranslation();
  const { ref, edges, measure } = useHorizontalOverflow<HTMLDivElement>({ enabled: Array.isArray(metrics?.items) && metrics.items.length > 0 });
  if (!metrics || !Array.isArray(metrics.items) || metrics.items.length === 0) return null;
  const items = metrics.items.slice(0, 5);
  const label = metrics.stale && metrics.staleLabel ? `${metrics.label} — ${metrics.staleLabel}` : metrics.label;
  const layout = size === 'compact' ? `items-center gap-x-3 ${edges.overflow ? 'justify-start' : 'justify-center'}`
    : size === 'large' ? `items-start gap-x-4 ${edges.overflow ? 'justify-start' : 'justify-center'}` : 'items-start gap-x-1';
  return (
    <div
      // A group, never a live region. These figures are re-read on a poll, and a `status` here made a
      // screen reader announce a register of environments every thirty seconds.
      role="group"
      aria-label={label}
      ref={ref}
      data-project-row-metrics
      data-size={size}
      data-overflow-left={String(edges.left)}
      data-overflow-right={String(edges.right)}
      // The entire track owns its gesture, including captions and gaps between actions. Do not cancel
      // native scrolling, but keep the card's long-press sensors and open handler out of it.
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onFocusCapture={(event) => {
        const item = (event.target as HTMLElement).closest<HTMLElement>('[data-metric]')!;
        revealHorizontalItem(event.currentTarget, item);
        measure();
      }}
      style={horizontalEdgeFadeStyle(edges)}
      data-refreshing={metrics.refreshing ? 'true' : undefined}
      data-stale={metrics.stale ? 'true' : undefined}
      className={`resource-meters flex flex-nowrap overflow-x-auto overflow-y-hidden overscroll-x-contain p-1 ${layout} min-w-0 transition-opacity duration-[var(--motion-base)] ${metrics.stale ? 'opacity-70' : metrics.refreshing ? 'opacity-80' : ''}`}
    >
      {items.map((item) => size === 'compact'
        ? <CompactResourceMeter key={item.id} item={item} locale={locale} />
        : <ResourceMeter key={item.id} item={item} dense={items.length >= 4} large={size === 'large'} locale={locale} />)}
    </div>
  );
}
